import type { WorkspaceResource } from "@/types_api/backend/transport/list_workspaces_response"
import {
  DEFAULT_WORKSPACE_ID,
  type parseLegacyOrganizationRoute,
  type parseWorkspaceRoute,
} from "@/utils/workspaceRoutes"

import {
  EMPTY_WORKSPACE_CONTEXT_VALUE,
  type WorkspaceContextValue,
} from "../contexts/WorkspaceContext"

type LegacyOrganizationRoute = NonNullable<
  ReturnType<typeof parseLegacyOrganizationRoute>
>
type WorkspaceRoute = NonNullable<ReturnType<typeof parseWorkspaceRoute>>

interface WorkspaceRouteResolution {
  contextValue: WorkspaceContextValue
  hasActiveDefaultWorkspace: boolean
  requestedWorkspaceAvailable: boolean
}

// The organization's active DEFAULT workspace, if the list carries one.
// `ensure_default_workspace` guarantees exactly one exists, so this is the
// single authoritative mapping from the `default` sentinel segment to a real
// workspace. URL resolution and the sentinel settle in
// `WorkspaceProviderRoute` both read it here so they cannot drift.
export function findActiveDefaultWorkspace(
  workspaceResources: readonly WorkspaceResource[],
): WorkspaceResource | undefined {
  return workspaceResources.find(
    (workspace) =>
      workspace.workspace_type === "DEFAULT" && workspace.status === "ACTIVE",
  )
}

export function resolveWorkspaceRoute({
  isWorkspacesEnabled,
  organizationRoute,
  workspaceRoute,
  workspaceResources,
}: {
  asPath: string
  isWorkspacesEnabled: boolean
  organizationRoute: LegacyOrganizationRoute | WorkspaceRoute | null
  workspaceRoute: WorkspaceRoute | null
  workspaceResources: readonly WorkspaceResource[]
}): WorkspaceRouteResolution {
  if (!isWorkspacesEnabled || !organizationRoute) {
    return {
      contextValue: EMPTY_WORKSPACE_CONTEXT_VALUE,
      hasActiveDefaultWorkspace: true,
      requestedWorkspaceAvailable: true,
    }
  }

  const workspaces = workspaceResources.filter(
    (workspace) =>
      workspace.status === "ACTIVE" || workspace.status === "ARCHIVED",
  )
  const defaultWorkspace = findActiveDefaultWorkspace(workspaceResources)
  // The segment is the workspace id. Match it against workspace.id. There is
  // no slug compatibility: workspaces are addressed by id only.
  const matchedWorkspace = workspaceRoute
    ? workspaces.find(
        (workspace) =>
          workspace.id === workspaceRoute.workspaceId &&
          workspace.status === "ACTIVE",
      )
    : undefined
  // The sentinel segment is the app's own placeholder for the active default
  // workspace: the proxy upgrades root, slug-less, and legacy organization
  // URLs to it. It is available exactly when the active default workspace
  // exists.
  const isDefaultSentinelSegment =
    workspaceRoute !== null &&
    workspaceRoute.workspaceId === DEFAULT_WORKSPACE_ID
  const currentWorkspace = workspaceRoute
    ? (matchedWorkspace ?? defaultWorkspace)
    : defaultWorkspace
  const requestedWorkspaceAvailable = workspaceRoute
    ? isDefaultSentinelSegment
      ? Boolean(defaultWorkspace)
      : Boolean(matchedWorkspace)
    : true

  return {
    contextValue: {
      currentWorkspace: currentWorkspace ?? null,
      routeWorkspaceId: workspaceRoute?.workspaceId ?? null,
      workspaces,
    },
    hasActiveDefaultWorkspace: Boolean(defaultWorkspace),
    requestedWorkspaceAvailable,
  }
}
